import React, { useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { GlassCard } from '../../../../../../components/glass';
import type { IContaCilMesStatus } from '../../../../../../hooks/useContaCilDashboard';
import type { IContaBancaria } from '../../../../../../hooks/useContaCilContasBancarias';
import type { IAgenciaBanco } from '../../../../../../hooks/useContaCilEmpresas';
import type { IContaCilQuebraItem } from '../../../../../../hooks/useContaCilLancamentos';
import { fmtValor } from '../../conciliar/shared';

// Paleta estável por banco (roxo do submódulo primeiro).
const PALETA_BANCO = ['#8B5CF6', '#6366F1', '#1B88F5', '#10B981', '#F59E0B', '#EC4899'];
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const fmtCompact = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return `${v.toFixed(0)}`;
};

const truncar = (s: string, max = 18) => (s.length > max ? `${s.slice(0, max)}…` : s);

/* ---------- Card-base do gráfico (header glass) ---------- */

function ChartCard({
  titulo,
  sub,
  children,
}: {
  titulo: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <GlassCard intensity="medium" p={5}>
      <Text
        fontSize="10px"
        fontWeight="700"
        color="#94A3B8"
        textTransform="uppercase"
        letterSpacing="0.06em"
      >
        {titulo}
      </Text>
      {sub && (
        <Text fontSize="13px" color="#64748B" mt={0.5}>
          {sub}
        </Text>
      )}
      <Box mt={4}>{children}</Box>
    </GlassCard>
  );
}

function SemDados() {
  return (
    <Flex h="120px" align="center" justify="center">
      <Text fontSize="12px" color="#94A3B8">
        Sem dados no recorte.
      </Text>
    </Flex>
  );
}

/* ---------- Tooltips glass ---------- */

interface TipProps {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string; payload?: any }>;
  label?: string | number;
}

const tipBoxSx = {
  bg: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(20px) saturate(180%)',
  sx: { WebkitBackdropFilter: 'blur(20px) saturate(180%)' as const },
  borderWidth: '1px',
  borderColor: 'rgba(255,255,255,0.6)',
  borderRadius: 'xl',
  px: 3,
  py: 2,
  boxShadow: '0 12px 40px -12px rgba(27,136,245,0.18)',
};

function GlassTooltipBar({ active, payload, label }: TipProps) {
  if (!active || !payload?.length) return null;
  return (
    <Box {...tipBoxSx}>
      <Text fontSize="11px" fontWeight="700" color="#0F172A" mb={1}>
        {label}
      </Text>
      {payload.map(p => (
        <Flex key={p.name} align="center" gap={1.5}>
          <Box w="8px" h="8px" borderRadius="full" bg={p.color} flexShrink={0} />
          <Text fontSize="11px" color="#64748B">
            {p.name}
          </Text>
          <Text fontSize="11px" fontWeight="700" color="#0F172A">
            R$ {fmtValor(p.value)}
          </Text>
        </Flex>
      ))}
    </Box>
  );
}

function GlassTooltipDonut({ active, payload, total }: TipProps & { total?: number }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const pct = total && total > 0 ? ((p.value ?? 0) / total) * 100 : 0;
  return (
    <Box {...tipBoxSx}>
      <Flex align="center" gap={1.5}>
        <Box w="8px" h="8px" borderRadius="full" bg={p.payload?.cor} flexShrink={0} />
        <Text fontSize="11px" fontWeight="700" color="#0F172A">
          {p.payload?.banco}
        </Text>
      </Flex>
      <Text fontSize="11px" color="#64748B" mt={0.5}>
        R$ {fmtValor(p.value)} · {pct.toFixed(1)}%
      </Text>
    </Box>
  );
}

/* ---------- 1. Donut: volume R$ por banco ---------- */

export function DonutBancos({ porBancoConta }: { porBancoConta: IContaCilQuebraItem[] }) {
  const dados = useMemo(() => {
    const map = new Map<string, number>();
    porBancoConta.forEach(i => {
      const banco = (i.chave?.split('|')[0] || i.rotulo || '—').trim();
      map.set(banco, (map.get(banco) || 0) + i.soma);
    });
    return Array.from(map.entries())
      .map(([banco, soma]) => ({ banco, soma }))
      .sort((a, b) => b.soma - a.soma)
      .map((d, idx) => ({ ...d, cor: PALETA_BANCO[idx % PALETA_BANCO.length] }));
  }, [porBancoConta]);

  const total = useMemo(() => dados.reduce((s, d) => s + d.soma, 0), [dados]);

  return (
    <ChartCard titulo="Volume por banco" sub="Quanto transita em cada banco">
      {dados.length === 0 ? (
        <SemDados />
      ) : (
        <Flex direction={{ base: 'column', md: 'row' }} align="center" gap={4}>
          <Box position="relative" w="180px" h="180px" flexShrink={0}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={dados}
                  dataKey="soma"
                  nameKey="banco"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={82}
                  stroke="#fff"
                  strokeWidth={2}
                  animationDuration={600}
                >
                  {dados.map(d => (
                    <Cell key={d.banco} fill={d.cor} />
                  ))}
                </Pie>
                <Tooltip content={<GlassTooltipDonut total={total} />} />
              </PieChart>
            </ResponsiveContainer>
            <Box
              position="absolute"
              inset={0}
              display="flex"
              flexDir="column"
              alignItems="center"
              justifyContent="center"
              pointerEvents="none"
            >
              <Text
                fontSize="9px"
                color="#94A3B8"
                textAlign="center"
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                total
              </Text>
              <Text
                fontSize="15px"
                fontWeight="700"
                color="#0F172A"
                textAlign="center"
                lineHeight="1"
              >
                R$ {fmtCompact(total)}
              </Text>
            </Box>
          </Box>
          <Flex direction="column" gap={2} flex={1} w="full">
            {dados.slice(0, 6).map(d => (
              <Flex key={d.banco} align="center" gap={2}>
                <Box w="9px" h="9px" borderRadius="full" bg={d.cor} flexShrink={0} />
                <Text
                  fontSize="12px"
                  color="#0F172A"
                  fontWeight="600"
                  isTruncated
                  flex={1}
                  title={d.banco}
                >
                  {d.banco}
                </Text>
                <Text fontSize="11px" color="#475569" fontWeight="600" flexShrink={0}>
                  R$ {fmtValor(d.soma)}
                </Text>
                <Text fontSize="10px" color="#94A3B8" minW="38px" textAlign="right" flexShrink={0}>
                  {total > 0 ? `${((d.soma / total) * 100).toFixed(0)}%` : '—'}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
    </ChartCard>
  );
}

/* ---------- 2. Mini-ranking CSS: nº de contas por banco ---------- */

export function RankingContasBanco({ contas }: { contas: IContaBancaria[] }) {
  const dados = useMemo(() => {
    const map = new Map<string, number>();
    contas.forEach(c => map.set(c.banco_nome, (map.get(c.banco_nome) || 0) + 1));
    return Array.from(map.entries())
      .map(([banco, qtd]) => ({ banco, qtd }))
      .sort((a, b) => b.qtd - a.qtd);
  }, [contas]);
  const max = Math.max(...dados.map(d => d.qtd), 1);

  return (
    <ChartCard titulo="Contas por banco" sub="Quantas contas em cada banco">
      {dados.length === 0 ? (
        <SemDados />
      ) : (
        <Flex direction="column" gap={2.5}>
          {dados.map(d => (
            <Box key={d.banco}>
              <Flex align="center" justify="space-between" gap={2} mb="2px">
                <Text fontSize="12px" color="#0F172A" fontWeight="600" isTruncated title={d.banco}>
                  {d.banco}
                </Text>
                <Text fontSize="11px" color="#475569" fontWeight="600" flexShrink={0}>
                  {d.qtd} {d.qtd === 1 ? 'conta' : 'contas'}
                </Text>
              </Flex>
              <Box h="6px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
                <Box h="full" w={`${(d.qtd / max) * 100}%`} borderRadius="full" bg="#6366F1" />
              </Box>
            </Box>
          ))}
        </Flex>
      )}
    </ChartCard>
  );
}

/* ---------- 3. Barra empilhada: conciliado vs a resolver por conta ---------- */

export function BarConciliacaoConta({ porBancoConta }: { porBancoConta: IContaCilQuebraItem[] }) {
  const dados = useMemo(
    () =>
      porBancoConta
        .map(i => {
          const conciliado = i.soma_conciliado || 0;
          const aResolver = Math.max(0, (i.soma || 0) - conciliado);
          return { conta: truncar(i.rotulo || i.chave || '—'), conciliado, aResolver };
        })
        .slice(0, 12),
    [porBancoConta],
  );
  const altura = Math.min(360, 56 + dados.length * 38);

  return (
    <ChartCard titulo="Conciliação por conta" sub="Conciliado vs a resolver (R$)">
      {dados.length === 0 ? (
        <SemDados />
      ) : (
        <>
          <Box h={`${altura}px`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dados}
                layout="vertical"
                margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
                barCategoryGap="28%"
              >
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#94A3B8' }}
                  tickFormatter={(v: number) => `R$ ${fmtCompact(v)}`}
                />
                <YAxis
                  type="category"
                  dataKey="conta"
                  width={130}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748B' }}
                />
                <Tooltip cursor={{ fill: 'rgba(99,102,241,0.06)' }} content={<GlassTooltipBar />} />
                <Bar
                  dataKey="conciliado"
                  name="conciliado"
                  stackId="s"
                  fill="#10B981"
                  maxBarSize={22}
                />
                <Bar
                  dataKey="aResolver"
                  name="a resolver"
                  stackId="s"
                  fill="#F59E0B"
                  radius={[0, 8, 8, 0]}
                  maxBarSize={22}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Flex gap={4} mt={2}>
            {[
              { c: '#10B981', l: 'conciliado' },
              { c: '#F59E0B', l: 'a resolver' },
            ].map(s => (
              <Flex key={s.l} align="center" gap={1.5}>
                <Box w="8px" h="8px" borderRadius="full" bg={s.c} />
                <Text fontSize="11px" color="#64748B">
                  {s.l}
                </Text>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </ChartCard>
  );
}

/* ---------- 3b. Barra: agência × banco (volume R$) ---------- */

export function AgenciaBancoChart({ dados }: { dados: IAgenciaBanco[] }) {
  const linhas = useMemo(() => {
    const bancos = Array.from(new Set(dados.map(d => d.banco)));
    return dados
      .map(d => ({
        rotulo: truncar(`${d.banco}${d.agencia ? ` · ag ${d.agencia}` : ' · s/ ag'}`, 22),
        soma: d.soma,
        cor: PALETA_BANCO[bancos.indexOf(d.banco) % PALETA_BANCO.length],
      }))
      .slice(0, 12);
  }, [dados]);
  const altura = Math.min(360, 56 + linhas.length * 34);

  return (
    <ChartCard titulo="Agência × banco" sub="Volume por agência em cada banco">
      {linhas.length === 0 ? (
        <Flex h="120px" align="center" justify="center" direction="column" gap={1}>
          <Text fontSize="12px" color="#94A3B8">
            Sem agência nos lançamentos ainda.
          </Text>
          <Text fontSize="11px" color="#CBD5E1">
            Popula conforme novos extratos chegam.
          </Text>
        </Flex>
      ) : (
        <Box h={`${altura}px`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={linhas}
              layout="vertical"
              margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
              barCategoryGap="28%"
            >
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#94A3B8' }}
                tickFormatter={(v: number) => `R$ ${fmtCompact(v)}`}
              />
              <YAxis
                type="category"
                dataKey="rotulo"
                width={150}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#64748B' }}
              />
              <Tooltip cursor={{ fill: 'rgba(99,102,241,0.06)' }} content={<GlassTooltipBar />} />
              <Bar dataKey="soma" name="volume" radius={[0, 8, 8, 0]} maxBarSize={20}>
                {linhas.map(l => (
                  <Cell key={l.rotulo} fill={l.cor} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </ChartCard>
  );
}

/* ---------- 4. Área: evolução do % conciliado por mês (nice) ---------- */

export function AreaEvolucaoConciliacao({ meses }: { meses: IContaCilMesStatus[] }) {
  const dados = useMemo(
    () =>
      meses.map(m => ({
        mes: MESES[m.mes - 1] ?? String(m.mes),
        pct: m.total > 0 ? Math.round((m.conciliados / m.total) * 100) : null,
      })),
    [meses],
  );
  const temDados = dados.some(d => d.pct != null);

  return (
    <ChartCard titulo="Evolução da conciliação" sub="% conciliado por mês (no ano)">
      {!temDados ? (
        <SemDados />
      ) : (
        <Box h="180px">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dados} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="gradConc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="mes"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8' }}
              />
              <YAxis
                domain={[0, 100]}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8' }}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip
                cursor={{ stroke: 'rgba(99,102,241,0.2)' }}
                formatter={(v: number) => [`${v}%`, 'conciliado']}
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.6)',
                  background: 'rgba(255,255,255,0.9)',
                  fontSize: 11,
                }}
              />
              <Area
                type="monotone"
                dataKey="pct"
                stroke="#6366F1"
                strokeWidth={2}
                fill="url(#gradConc)"
                connectNulls
                dot={false}
                activeDot={{ r: 4 }}
                animationDuration={600}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </ChartCard>
  );
}
